// Swak Custom - ウェルカムモーダル
import { Component } from '@theme/component';

/**
 * ウェルカムモーダルコンポーネント
 * サイト訪問時に条件に応じてモーダルを表示する
 * 
 * @extends {Component}
 */
class SwakWelcomeModal extends Component {
  // ストレージキー定義
  static STORAGE_KEY_CLOSED = 'swak_welcome_modal_closed';
  static STORAGE_KEY_SESSION_TIME = 'swak_welcome_modal_session_time';
  static STORAGE_KEY_LAST_UPDATE = 'swak_welcome_modal_last_update';

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {number} */
  #delaySeconds = 0;

  /** @type {number} */
  #scrollPercentage = 0;

  /** @type {number} */
  #suppressDays = 0;

  /** @type {boolean} */
  #testMode = false;

  /** @type {string} */
  #closePosition = 'top-right';

  /** @type {number} */
  #closeSize = 40;

  /** @type {number} */
  #closeBorderRadius = 20;

  /** @type {string} */
  #closeTextColor = '#000000';

  /** @type {string} */
  #closeBgColor = '#FFFFFF';

  /** @type {number} */
  #closeBgOpacity = 95;

  /** @type {string} */
  #closeBorderColor = '#E5E5E5';

  /** @type {number} */
  #closeBorderWidth = 0;

  /** @type {number} */
  #closeOffsetTop = 12;

  /** @type {number} */
  #closeOffsetRight = 12;

  /** @type {number} */
  #closeOffsetBottom = 16;

  /** @type {number} */
  #closeOffsetLeft = 0;

  /** @type {number} */
  #imageBorderRadius = 8;

  /** @type {string | null} */
  #excludeParamKey = null;

  /** @type {boolean} */
  #forceShow = false;

  /** @type {boolean} */
  #forceHide = false;

  /** @type {boolean} */
  #delayConditionMet = false;

  /** @type {boolean} */
  #scrollConditionMet = false;

  /** @type {number} */
  #sessionStartTime = 0;

  /** @type {number} */
  #accumulatedTime = 0;

  /** @type {number | null} */
  #intervalId = null;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    // ダイアログ要素の取得
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    // 設定値の読み込み
    this.#loadSettings();

    // URLパラメータチェック
    this.#checkUrlParam();

    // 強制非表示の場合は何もしない
    if (this.#forceHide) {
      return;
    }

    // 強制即表示の場合は即座に表示
    if (this.#forceShow) {
      this.#showModal();
      return;
    }

    // テストモードが有効な場合は抑制チェックをスキップ
    if (!this.#testMode) {
      // 既に閉じられているかチェック
      if (this.#isModalSuppressed()) {
        return;
      }
    }

    // 表示条件の監視開始
    this.#startMonitoring();
  }

  disconnectedCallback() {
    this.#stopMonitoring();
  }

  /**
   * 設定値の読み込み
   */
  #loadSettings() {
    this.#delaySeconds = parseInt(this.dataset.delay || '5', 10);
    this.#scrollPercentage = parseInt(this.dataset.scroll || '30', 10);
    this.#testMode = this.dataset.testMode === 'true';
    this.#suppressDays = parseInt(this.dataset.suppressDays || '3', 10);
    this.#imageBorderRadius = parseInt(this.dataset.imageBorderRadius || '8', 10);
    this.#closePosition = this.dataset.closePosition || 'top-right';
    this.#closeSize = parseInt(this.dataset.closeSize || '40', 10);
    this.#closeBorderRadius = parseInt(this.dataset.closeBorderRadius || '20', 10);
    this.#closeTextColor = this.dataset.closeTextColor || '#000000';
    this.#closeBgColor = this.dataset.closeBgColor || '#FFFFFF';
    this.#closeBgOpacity = parseInt(this.dataset.closeBgOpacity || '95', 10);
    this.#closeBorderColor = this.dataset.closeBorderColor || '#E5E5E5';
    this.#closeBorderWidth = parseInt(this.dataset.closeBorderWidth || '0', 10);
    this.#closeOffsetTop = parseInt(this.dataset.closeOffsetTop || '12', 10);
    this.#closeOffsetRight = parseInt(this.dataset.closeOffsetRight || '12', 10);
    this.#closeOffsetBottom = parseInt(this.dataset.closeOffsetBottom || '16', 10);
    this.#closeOffsetLeft = parseInt(this.dataset.closeOffsetLeft || '0', 10);
    this.#excludeParamKey = this.dataset.excludeParamKey || null;

    // 閉じるボタンのデザイン設定を適用
    this.#applyCloseButtonDesign();
  }

  /**
   * URLパラメータによる表示制御チェック
   */
  #checkUrlParam() {
    if (!this.#excludeParamKey) return;

    const params = new URLSearchParams(window.location.search);
    const paramValue = params.get(this.#excludeParamKey);

    if (paramValue === null) return;

    // true = 強制即表示、false = 強制非表示
    if (paramValue === 'true') {
      this.#forceShow = true;
    } else if (paramValue === 'false') {
      this.#forceHide = true;
    }
  }

  /**
   * 閉じるボタンのデザイン設定を適用
   */
  #applyCloseButtonDesign() {
    // 背景色のRGBA変換
    const bgColor = this.#hexToRgb(this.#closeBgColor);
    const bgOpacity = this.#closeBgOpacity / 100;
    const bgColorWithOpacity = `rgb(${bgColor.r} ${bgColor.g} ${bgColor.b} / ${bgOpacity})`;
    const bgColorHover = `rgb(${bgColor.r} ${bgColor.g} ${bgColor.b} / 1)`;

    // CSS カスタムプロパティを設定
    this.style.setProperty('--swak-image-border-radius', `${this.#imageBorderRadius}px`);
    this.style.setProperty('--swak-close-size', `${this.#closeSize}px`);
    this.style.setProperty('--swak-close-text-color', this.#closeTextColor);
    this.style.setProperty('--swak-close-bg-color', bgColorWithOpacity);
    this.style.setProperty('--swak-close-bg-color-hover', bgColorHover);
    this.style.setProperty('--swak-close-border-width', `${this.#closeBorderWidth}px`);
    this.style.setProperty('--swak-close-border-color', this.#closeBorderColor);
    this.style.setProperty('--swak-close-border-radius', `${this.#closeBorderRadius}px`);
    this.style.setProperty('--swak-close-offset-top', `${this.#closeOffsetTop}px`);
    this.style.setProperty('--swak-close-offset-right', `${this.#closeOffsetRight}px`);
    this.style.setProperty('--swak-close-offset-bottom', `${this.#closeOffsetBottom}px`);
    this.style.setProperty('--swak-close-offset-left', `${this.#closeOffsetLeft}px`);
  }

  /**
   * HEX色をRGBオブジェクトに変換
   * @param {string} hex - HEX形式の色コード
   * @returns {{r: number, g: number, b: number}}
   */
  #hexToRgb(hex) {
    // # を除去
    const cleanHex = hex.replace('#', '');

    // 3桁の短縮形式を6桁に展開
    const fullHex = cleanHex.length === 3
      ? cleanHex.split('').map(c => c + c).join('')
      : cleanHex;

    const r = parseInt(fullHex.substring(0, 2), 16);
    const g = parseInt(fullHex.substring(2, 4), 16);
    const b = parseInt(fullHex.substring(4, 6), 16);

    return { r, g, b };
  }

  /**
   * モーダルが抑制期間中かチェック
   * @returns {boolean}
   */
  #isModalSuppressed() {
    const closedTimestamp = localStorage.getItem(SwakWelcomeModal.STORAGE_KEY_CLOSED);
    if (!closedTimestamp) return false;

    const closedDate = new Date(parseInt(closedTimestamp, 10));
    const now = new Date();
    const daysPassed = (now.getTime() - closedDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysPassed < this.#suppressDays;
  }

  /**
   * 表示条件の監視を開始
   */
  #startMonitoring() {
    // セッション累積時間の初期化
    this.#initializeSessionTime();

    // 両方の条件が0の場合は即座に表示
    if (this.#delaySeconds === 0 && this.#scrollPercentage === 0) {
      this.#showModal();
      return;
    }

    // 滞在時間条件の監視
    if (this.#delaySeconds > 0) {
      this.#startTimeMonitoring();
    } else {
      this.#delayConditionMet = true;
    }

    // スクロール条件の監視
    if (this.#scrollPercentage > 0) {
      this.#startScrollMonitoring();
    } else {
      this.#scrollConditionMet = true;
    }

    // 初回スクロールチェック（既にスクロール済みの場合）
    if (this.#scrollPercentage > 0) {
      this.#checkScrollCondition();
    }
  }

  /**
   * セッション累積時間の初期化
   */
  #initializeSessionTime() {
    const storedTime = sessionStorage.getItem(SwakWelcomeModal.STORAGE_KEY_SESSION_TIME);
    const lastUpdate = sessionStorage.getItem(SwakWelcomeModal.STORAGE_KEY_LAST_UPDATE);

    this.#accumulatedTime = storedTime ? parseFloat(storedTime) : 0;
    this.#sessionStartTime = Date.now();

    // 前回の更新時刻がある場合、非アクティブ時間は累積しない
    if (lastUpdate) {
      const timeSinceLastUpdate = Date.now() - parseInt(lastUpdate, 10);
      // 5分以上経過している場合は新しいセッションとみなす
      if (timeSinceLastUpdate > 5 * 60 * 1000) {
        this.#accumulatedTime = 0;
      }
    }
  }

  /**
   * 滞在時間の監視を開始
   */
  #startTimeMonitoring() {
    // 1秒ごとに累積時間を更新
    this.#intervalId = setInterval(() => {
      const currentTime = Date.now();
      const elapsedSeconds = (currentTime - this.#sessionStartTime) / 1000;
      const totalSeconds = this.#accumulatedTime + elapsedSeconds;

      // sessionStorageに保存
      sessionStorage.setItem(SwakWelcomeModal.STORAGE_KEY_SESSION_TIME, totalSeconds.toString());
      sessionStorage.setItem(SwakWelcomeModal.STORAGE_KEY_LAST_UPDATE, currentTime.toString());

      // 条件達成チェック
      if (totalSeconds >= this.#delaySeconds) {
        this.#delayConditionMet = true;
        this.#checkAndShowModal();
      }
    }, 1000);
  }

  /**
   * スクロール監視を開始
   */
  #startScrollMonitoring() {
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  /**
   * スクロールイベントハンドラー
   */
  #handleScroll = () => {
    if (this.#scrollConditionMet) return;
    this.#checkScrollCondition();
  };

  /**
   * スクロール条件をチェック
   */
  #checkScrollCondition() {
    const scrollY = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const maxScroll = documentHeight - windowHeight;

    if (maxScroll <= 0) {
      // スクロール不可能な場合は条件達成とみなす
      this.#scrollConditionMet = true;
      this.#checkAndShowModal();
      return;
    }

    const currentScrollPercentage = (scrollY / maxScroll) * 100;

    if (currentScrollPercentage >= this.#scrollPercentage) {
      this.#scrollConditionMet = true;
      this.#checkAndShowModal();
    }
  }

  /**
   * 条件をチェックしてモーダルを表示
   */
  #checkAndShowModal() {
    if (this.#delayConditionMet || this.#scrollConditionMet) {
      this.#showModal();
    }
  }

  /**
   * モーダルを表示
   */
  #showModal() {
    if (!this.#dialog) return;

    // 監視を停止
    this.#stopMonitoring();

    // スクロール位置を保存してbodyを固定
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    // モーダルを表示
    this.#dialog.showModal();

    // ESCキーでの閉じる処理
    this.#dialog.addEventListener('cancel', this.#handleCancel);

    // バックドロップクリックでの閉じる処理
    this.#dialog.addEventListener('click', this.#handleBackdropClick);
  }

  /**
   * モーダルを閉じる（パブリックメソッド）
   */
  closeModal() {
    this.#closeModalWithAnimation();
  }

  /**
   * アニメーション付きでモーダルを閉じる
   */
  async #closeModalWithAnimation() {
    if (!this.#dialog) return;

    // 閉じるアニメーション用のクラスを追加
    this.#dialog.classList.add('swak-closing');

    // アニメーション完了を待つ
    await new Promise((resolve) => {
      const handleAnimationEnd = () => {
        this.#dialog?.removeEventListener('animationend', handleAnimationEnd);
        resolve(null);
      };
      this.#dialog?.addEventListener('animationend', handleAnimationEnd);
    });

    // モーダルを閉じる
    this.#dialog.close();
    this.#dialog.classList.remove('swak-closing');

    // bodyの固定を解除してスクロール位置を復元
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    // 閉じた時刻を保存
    localStorage.setItem(SwakWelcomeModal.STORAGE_KEY_CLOSED, Date.now().toString());

    // セッションストレージをクリア
    sessionStorage.removeItem(SwakWelcomeModal.STORAGE_KEY_SESSION_TIME);
    sessionStorage.removeItem(SwakWelcomeModal.STORAGE_KEY_LAST_UPDATE);

    // イベントリスナーをクリーンアップ
    this.#dialog.removeEventListener('cancel', this.#handleCancel);
    this.#dialog.removeEventListener('click', this.#handleBackdropClick);
  }

  /**
   * キャンセルイベントハンドラー(ESCキー)
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.#closeModalWithAnimation();
  };

  /**
   * バックドロップクリックハンドラー
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (event.target === this.#dialog) {
      this.#closeModalWithAnimation();
    }
  };

  /**
   * 監視を停止
   */
  #stopMonitoring() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
    window.removeEventListener('scroll', this.#handleScroll);
  }
}

// カスタム要素として登録
if (!customElements.get('swak-welcome-modal')) {
  customElements.define('swak-welcome-modal', SwakWelcomeModal);
}

// /Swak Custom - ウェルカムモーダル
